import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import BarChart01Icon from "@redotech/redo-icons/bar-chart-01.svg";
import BarChart01FilledIcon from "@redotech/redo-icons/bar-chart-01_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import DatabaseIcon from "@redotech/redo-icons/database-02.svg";
import DatabaseFilledIcon from "@redotech/redo-icons/database-02_filled.svg";
import LayoutGrid01Icon from "@redotech/redo-icons/layout-grid-01.svg";
import LayoutGrid01FilledIcon from "@redotech/redo-icons/layout-grid-01_filled.svg";
import Map01Icon from "@redotech/redo-icons/map-01.svg";
import Map01FilledIcon from "@redotech/redo-icons/map-01_filled.svg";
import Route01Icon from "@redotech/redo-icons/route.svg";
import Route01FilledIcon from "@redotech/redo-icons/route_filled.svg";
import Tool01Icon from "@redotech/redo-icons/tool-01.svg";
import Tool01FilledIcon from "@redotech/redo-icons/tool-01_filled.svg";
import Truck01Icon from "@redotech/redo-icons/truck-01.svg";
import Truck01FilledIcon from "@redotech/redo-icons/truck-01_filled.svg";
import ZapIcon from "@redotech/redo-icons/zap.svg";
import ZapFilledIcon from "@redotech/redo-icons/zap_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import type { SettingsPageId } from "@redotech/redo-settings-common/settings-visibility/settings-pages";
import type { SettingItem } from "@redotech/redo-web/navigation/redo-settings-nav";

type SharedSettingsPage = Extract<
  SettingsPageId,
  | "order-fulfillment/order-editing"
  | "order-fulfillment/automations"
  | "order-fulfillment/reports"
  | "order-fulfillment/scanning-warehouse"
  | "order-fulfillment/warehouse-dashboard"
  | "order-fulfillment/warehouse-layouts"
  | "order-fulfillment/work-order-flows"
  | "shipping/carriers"
  | "shipping/freight"
>;

/** Nav items both settings shells show; each shell decides placement and visibility. */
export function useSharedSettingsItems(): Record<
  SharedSettingsPage,
  SettingItem
> {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  return {
    "order-fulfillment/order-editing": {
      id: "order-editing-button",
      href: routes.getSettingsSubRoute("order-management/order-editing"),
      friendlyName: t("Order editing", {
        context: "merchant settings label for order editing",
      }),
      searchTerms: ["order holds", "editing rules"],
      UnfilledIcon: Tool01Icon,
      FilledIcon: Tool01FilledIcon,
    },
    "order-fulfillment/automations": {
      id: "automations-oms",
      href: routes.getSettingsSubRoute("oms/automations"),
      friendlyName: t("Automations", {
        context: "merchant settings label for order fulfillment automations",
      }),
      UnfilledIcon: ZapIcon,
      FilledIcon: ZapFilledIcon,
    },
    "order-fulfillment/reports": {
      id: "reports",
      href: routes.getSettingsSubRoute("oms/reports"),
      friendlyName: t("Reports", {
        context: "merchant settings label for order fulfillment reports",
      }),
      UnfilledIcon: BarChart01Icon,
      FilledIcon: BarChart01FilledIcon,
    },
    "order-fulfillment/scanning-warehouse": {
      id: "scanning-warehouse",
      href: routes.getSettingsSubRoute("oms/scanning-warehouse"),
      friendlyName: t("Warehouse"),
      searchTerms: [
        "scanning",
        "Scanning & warehouse",
        "Scanning and warehouse",
      ],
      searchMatches: [
        {
          label: t("Warehouse"),
          searchTerms: ["Scanning & warehouse", "Scanning and warehouse"],
        },
      ],
      UnfilledIcon: DatabaseIcon,
      FilledIcon: DatabaseFilledIcon,
    },
    "order-fulfillment/warehouse-dashboard": {
      id: "warehouse-dashboard",
      href: routes.getSettingsSubRoute("oms/warehouse-dashboard"),
      friendlyName: t("Warehouse dashboard", {
        context:
          "merchant settings label for the fulfillment warehouse dashboard",
      }),
      UnfilledIcon: DashboardIcon,
      FilledIcon: DashboardFilledIcon,
    },
    "order-fulfillment/warehouse-layouts": {
      id: "warehouse-layouts",
      href: routes.getSettingsSubRoute("oms/warehouse-layouts"),
      friendlyName: t("Warehouse layouts", {
        context: "merchant settings label for fulfillment warehouse layouts",
      }),
      UnfilledIcon: LayoutGrid01Icon,
      FilledIcon: LayoutGrid01FilledIcon,
    },
    "order-fulfillment/work-order-flows": {
      id: "work-order-flows",
      href: routes.getSettingsSubRoute("oms/work-order-flows"),
      friendlyName: t("Work order flows", {
        context: "merchant settings label for fulfillment work order flows",
      }),
      UnfilledIcon: Route01Icon,
      FilledIcon: Route01FilledIcon,
    },
    "shipping/carriers": {
      id: "carriers-link",
      href: routes.getSettingsSubRoute("carriers"),
      friendlyName: t("Carriers", {
        context: "merchant settings label for shipping carriers",
      }),
      UnfilledIcon: Truck01Icon,
      FilledIcon: Truck01FilledIcon,
    },
    "shipping/freight": {
      id: "freight-link",
      href: routes.getSettingsSubRoute("freight"),
      friendlyName: t("Freight", {
        context: "merchant settings label for freight shipping",
      }),
      UnfilledIcon: Map01Icon,
      FilledIcon: Map01FilledIcon,
    },
  };
}
